import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Mic2, Plus, Server, Zap } from 'lucide-react';
import { useVoiceSettings, useVoiceStatus, useStartVoice } from '@/hooks/useVoiceApi';
import { formatInactivityMinutes } from '@/components/InactivityTimeoutControl';
import { PersonaEnrollmentDialog } from '@/components/PersonaEnrollmentDialog';
import { UserMenu } from '@/components/UserMenu';
import type { VoiceStatus } from '@/types';

function friendlyStatus(status: string): string {
  const map: Record<string, string> = {
    InService: 'running',
    Creating: 'starting',
    Updating: 'updating',
    Deleting: 'stopping',
    Failed: 'failed',
    NotFound: 'stopped',
    Unknown: 'unknown',
    pending: 'starting',
    running: 'running',
    stopped: 'stopped',
    stopping: 'stopping',
    terminated: 'terminated',
  };
  return map[status] ?? status.toLowerCase();
}

function StatusBadge({ label, status }: { label: string; status: string }) {
  const isReady = status === 'InService' || status === 'running';
  const isStarting = status === 'Creating' || status === 'pending';
  return (
    <div className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-4 py-2">
      <span
        className={[
          'h-2 w-2 rounded-full',
          isReady ? 'bg-green-400' : isStarting ? 'bg-yellow-400 animate-pulse' : 'bg-red-500',
        ].join(' ')}
      />
      <span className="text-sm text-white/70">{label}</span>
      <span className="ml-auto text-xs text-white/40">{friendlyStatus(status)}</span>
    </div>
  );
}

function isAllReady(status: VoiceStatus) {
  return (
    status.asr === 'InService' &&
    status.tts === 'InService' &&
    status.livekit === 'running'
  );
}

function isAnyStarting(status: VoiceStatus) {
  return (
    status.asr === 'Creating' ||
    status.tts === 'Creating' ||
    status.livekit === 'pending'
  );
}

export function StatusGatePage() {
  const navigate = useNavigate();
  const { data: status, isLoading } = useVoiceStatus();
  const { data: settings } = useVoiceSettings();
  const startVoice = useStartVoice();
  const [inactivityMinutes, setInactivityMinutes] = useState<number>();
  const [showEnrollment, setShowEnrollment] = useState(false);
  const [enrollmentComplete, setEnrollmentComplete] = useState(false);

  useEffect(() => {
    if (status && isAllReady(status) && !showEnrollment) {
      navigate('/personas');
    }
  }, [status, navigate, showEnrollment]);

  useEffect(() => {
    if (settings) setInactivityMinutes(settings.inactivityMinutes);
  }, [settings]);

  useEffect(() => {
    if (!enrollmentComplete) return undefined;
    const timeout = window.setTimeout(() => setEnrollmentComplete(false), 6_000);
    return () => window.clearTimeout(timeout);
  }, [enrollmentComplete]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-8 bg-black p-6">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 flex items-center justify-between border-b border-white/10 px-4 py-3">
        <div className="flex items-center gap-2">
          <Mic2 className="h-5 w-5 text-white" />
          <span className="font-medium text-white">Twin Agent Voice</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowEnrollment(true)} className="flex h-8 w-8 items-center justify-center rounded-full border border-white/10 text-white/70 transition hover:border-white/30 hover:text-white" aria-label="Enroll a voice persona" title="Enroll a voice persona"><Plus className="h-4 w-4" /></button>
          <UserMenu />
        </div>
      </div>
      {/* Content */}
      <div className="text-center">
        <div className="mb-3 flex items-center justify-center gap-2">
          <Mic2 className="h-8 w-8 text-white" />
          <h1 className="text-2xl font-semibold text-white">Twin Agent Voice</h1>
        </div>
        <p className="text-sm text-white/50">Real-time audio conversations with digital twins</p>
      </div>

      {/* Service status */}
      <div className="w-full max-w-sm space-y-2">
        {isLoading || !status ? (
          <div className="flex justify-center py-4">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-white border-t-transparent" />
          </div>
        ) : (
          <>
            <StatusBadge label="Speech Recognition" status={status.asr} />
            <StatusBadge label="Voice Synthesis" status={status.tts} />
            <StatusBadge label="LiveKit Server" status={status.livekit} />
          </>
        )}
      </div>

      {/* Action */}
      {status && !isAllReady(status) && (
        <div className="flex flex-col items-center gap-3">
          {isAnyStarting(status) ? (
            <div className="flex items-center gap-2 text-yellow-400">
              <Server className="h-4 w-4 animate-pulse" />
              <span className="text-sm">Services starting… this takes ~7–8 min</span>
            </div>
          ) : (
            <>
              {settings && inactivityMinutes && (
                <label className="flex items-center gap-2 text-sm text-white/60">
                  <span>Auto-shutdown</span>
                  <select
                    value={inactivityMinutes}
                    onChange={(event) => setInactivityMinutes(Number(event.target.value))}
                    className="cursor-pointer bg-transparent text-white outline-none"
                  >
                    {settings.allowedInactivityMinutes.map((minutes) => (
                      <option key={minutes} value={minutes} className="bg-zinc-900 text-white">
                        {formatInactivityMinutes(minutes)}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <button
                onClick={() => inactivityMinutes && startVoice.mutate(inactivityMinutes)}
                disabled={startVoice.isPending || !inactivityMinutes}
                className="flex items-center gap-2 rounded-xl bg-white px-8 py-3 font-medium text-black transition hover:bg-white/90 disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed"
              >
                <Zap className="h-4 w-4" />
                {startVoice.isPending ? 'Starting…' : 'Start Voice Services'}
              </button>
            </>
          )}
          <p className="text-xs text-white/30">
            Services auto-shutdown after {inactivityMinutes ? formatInactivityMinutes(inactivityMinutes) : '...'} of inactivity
          </p>
        </div>
      )}
      {enrollmentComplete && <div role="status" className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-lg border border-green-400/30 bg-zinc-900 px-4 py-3 text-sm text-green-300 shadow-xl">New persona enrolled. It will be available after the next voice-services startup.</div>}
      {showEnrollment && <PersonaEnrollmentDialog onClose={() => setShowEnrollment(false)} onReady={() => setEnrollmentComplete(true)} />}
    </div>
  );
}
